import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, Monitor, Tablet, ShieldAlert, Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { CARD, Label } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ICON = { mobile: Smartphone, tablette: Tablet, ordinateur: Monitor };
const fmt = (s) => new Date(s).toLocaleString("fr-FR", { timeZone: "America/Martinique", dateStyle: "short", timeStyle: "short" });

function Stat({ label, value, tone, testid }) {
  return <div className={`${CARD} p-4`} data-testid={testid}><Label>{label}</Label><p className={`mt-1 font-display text-2xl font-black ${tone}`}>{value}</p></div>;
}

function Row({ e }) {
  const Icon = ICON[e.device_kind] || Monitor;
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 border-b border-gray-50 py-3 ${e.success ? "" : "bg-rose-50/50"}`} data-testid={`login-event-${e.id}`}>
      <div className="flex min-w-0 items-center gap-3">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${e.success ? "bg-emerald-50 text-[#065F46]" : "bg-rose-100 text-rose-600"}`}>{e.success ? <Icon className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}</span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{e.user_name || e.email}{!e.success && <span className="ml-2 text-xs font-bold text-rose-600">Échec : {e.reason}</span>}
            {e.new_device && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700" data-testid="new-device-badge"><Sparkles className="h-3 w-3" />Nouvel appareil</span>}</p>
          <p className="truncate text-xs text-gray-400">{e.device} · {e.method === "google" ? "Google" : "Mot de passe"} · IP {e.ip}{e.user_name ? ` · ${e.email}` : ""}</p>
        </div>
      </div>
      <span className="shrink-0 text-xs font-medium text-gray-500">{fmt(e.at)}</span>
    </div>
  );
}

export function LoginJournal({ users }) {
  const [f, setF] = useState({ user_id: "all", status: "all", start: "", end: "" });
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    const p = { ...(f.user_id !== "all" && { user_id: f.user_id }), ...(f.status !== "all" && { status: f.status }), ...(f.start && { start: f.start }), ...(f.end && { end: f.end }) };
    api.get("/login-journal", { params: p }).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const setRet = async (v) => { try { await api.put("/login-journal/settings", { retention_months: Number(v) }); toast.success("Durée de conservation enregistrée"); load(); } catch (e) { toast.error(apiErr(e)); } };
  if (!d) return null;
  return (
    <div className="mt-5 space-y-4" data-testid="login-journal">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Connexions (30 j)" value={d.stats.total_30d} tone="text-gray-900" testid="lj-total" />
        <Stat label="Échecs (30 j)" value={d.stats.failed_30d} tone={d.stats.failed_30d ? "text-rose-600" : "text-gray-900"} testid="lj-failed" />
        <Stat label="Nouveaux appareils (30 j)" value={d.stats.new_devices_30d} tone={d.stats.new_devices_30d ? "text-amber-600" : "text-gray-900"} testid="lj-new" />
      </div>
      <div className={`${CARD} flex flex-wrap items-center gap-2 p-3`}>
        <Select value={f.user_id} onValueChange={(v) => setF({ ...f, user_id: v })}>
          <SelectTrigger data-testid="lj-user" className="w-48 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Toutes les personnes</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
          <SelectTrigger data-testid="lj-status" className="w-40 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Toutes</SelectItem><SelectItem value="success">Réussies</SelectItem><SelectItem value="failed">Échouées</SelectItem></SelectContent>
        </Select>
        <Input data-testid="lj-start" type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="w-40 rounded-full" aria-label="Du" />
        <Input data-testid="lj-end" type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="w-40 rounded-full" aria-label="Au" />
        <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">Conservation
          <Select value={String(d.retention_months)} onValueChange={setRet}>
            <SelectTrigger data-testid="lj-retention" className="h-9 w-28 rounded-full"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="12">12 mois</SelectItem><SelectItem value="24">24 mois</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className={`${CARD} px-4 sm:px-6`}>
        {d.items.length === 0 ? <p className="py-6 text-sm text-gray-400">Aucune connexion sur cette période.</p> : d.items.map((e) => <Row key={e.id} e={e} />)}
      </div>
      <p className="text-[11px] text-gray-400">Heures affichées en heure de la Martinique. Les tentatives avec une adresse inconnue du cabinet ne sont pas rattachées au journal.</p>
    </div>
  );
}
